import { cache } from '@/utils/cacheManager'
import * as NumberUtils from '@/utils/NumberUtils'

export class EstimateProfitUtil {
  static _job = null
  /**
   * 是否验证预估利润
   */
  static _isVaildationEstimate = false
  /**
   * 利润率
   */
  static _estimateProfitRate = 0

  static async checkEstimate(job, openEstimateValidationDialog) {
    this._job = job
    this._isVaildationEstimate = parseInt(cache.config.get('isVaildationEstimate')) === 1
    this._estimateProfitRate = parseInt(cache.config.get('estimateProfitRate'))
    // 判断主票新增小票后，编辑小票，判断费用是否都是新创建的，如果是，对小票进行预估计算以及利润率验证
    let isNewCharge = false
    let index = 0
    if (job.lclMasterUuid && job.charges && job.charges.length > 0 && job.uuid) {
      for (const c of job.charges) {
        if (!c.uuid) {
          index++
        }
      }
      if (index === job.charges.length) {
        isNewCharge = true
      }
    }
    if (this._isVaildationEstimate && this.profitIsExceed() && !job.situation && (!job.uuid || isNewCharge)) {
      if (typeof openEstimateValidationDialog === 'function') {
        const result = openEstimateValidationDialog({
          estimateProfitRate: this._estimateProfitRate,
          job,
          tatolProfit: this.getGrossProfitRate()
        })
        if (result instanceof Promise) {
          return await result
        }
        return result
      } else {
        return true
      }
    } else {
      return true
    }
  }
  /**
   * 当利润率超过 系统配置参数值时或者利润为负值时
   */
  static profitIsExceed() {
    if (this._job.estimateGrossProfit <= 0) {
      return true
    }
    const rate = this.getGrossProfitRate()
    if (rate > this._estimateProfitRate || rate <= 0) {
      return true
    }
    return false
  }
  /**
   * 计算利润率 (收入-成本) / 收入 × 100%
   */
  static getGrossProfitRate() {
    if (this._job.estimateAccountsReceivable === 0) {
      return this._job.estimateAccountsReceivable
    }
    return (this._job.estimateAccountsReceivable - this._job.estimateAccountsPayable) / this._job.estimateAccountsReceivable * 100
  }

  /**
   * 计算业务的预估信息
   * @param job
   * @param charges
   */
  static calculateEstimatedInformation(job = null, charges = []) {
    if (!job || job.uuid || !charges || charges.length === 0) {
      return
    }
    // 记录每个币种对应的原币种金额汇总
    const arCurrencyTotalAmountMap = new Map()
    const apCurrencyTotalAmountMap = new Map()
    // 记录每个币种对应的汇率
    const arRateMap = new Map()
    const apRateMap = new Map()
    // 记录每个币种对应的费用集合
    const currencyCharges = new Map()
    // 记录所有的币种
    const currencyList = []
    // 1.组装数据
    for (const c of charges) {
      // 收付类型
      const receivable = c.receivable
      // 币种
      const currency = c.currency
      // 含税价
      const amount = c.amount
      // 汇率
      const exchangeRate = c.exchangeRate
      if (!amount || !exchangeRate || !currency) {
        continue
      }
      // 应收原币合计
      let arTotal = arCurrencyTotalAmountMap.get(currency) || 0.0
      // 应付原币合计
      let apTotal = apCurrencyTotalAmountMap.get(currency) || 0.0
      // 应收各个币种汇率集合
      const arRateList = arRateMap.get(currency) || []
      // 应付各个币种汇率集合
      const apRateList = apRateMap.get(currency) || []
      // 记录需要参与计算的费用集合
      const calChargeList = currencyCharges.get(currency) || []
      calChargeList.push(c)
      currencyCharges.set(currency, calChargeList)
      // 所有币种集合
      if (!currencyList.includes(currency)) {
        currencyList.push(currency)
      }
      if (receivable) {
        arTotal = NumberUtils.ToFixed2(arTotal + amount, 2)
        if (!arRateList.includes(exchangeRate)) {
          arRateList.push(exchangeRate)
        }
        arCurrencyTotalAmountMap.set(currency, arTotal)
        arRateMap.set(currency, arRateList)
      } else {
        apTotal = NumberUtils.ToFixed2(apTotal + amount, 2)
        if (!apRateList.includes(exchangeRate)) {
          apRateList.push(exchangeRate)
        }
        apCurrencyTotalAmountMap.set(currency, apTotal)
        apRateMap.set(currency, apRateList)
      }
    }
    // 2.循环当前业务费用包含的币种, 判断每个币种的汇率是否都相同,以及应收应付原币种总金额是否相同
    const sameCurrencyRateMap = new Map()
    for (const c of currencyList) {
      const arRateList = arRateMap.get(c)
      const apRateList = apRateMap.get(c)
      if (arRateList && apRateList &&
          arRateList.length === 1 && apRateList.length === 1 && arRateList[0] === apRateList[0]) {
        const arTotal = arCurrencyTotalAmountMap.get(c) || 0.0
        const apTotal = apCurrencyTotalAmountMap.get(c) || 0.0
        if (arTotal === apTotal) {
          sameCurrencyRateMap.set(c, Boolean.TRUE)
        }
      }
    }
    // 3.循环当前业务费用包含的币种，计算对应的金额
    let revenueSum = 0.0
    let costSum = 0.0
    for (const c of currencyList) {
      if (sameCurrencyRateMap.has(c)) {
        // 应收应付原币种总金额以及汇率一致时，取应收的第一笔汇率
        const arRateList = arRateMap.get(c)
        const rate = arRateList[0]
        // 取应收的原币种总金额
        const arTotal = arCurrencyTotalAmountMap.get(c) || 0.0
        // 总收入
        revenueSum += NumberUtils.ToFixed2(arTotal * rate, 2)
        // 总成本
        costSum += NumberUtils.ToFixed2(arTotal * rate, 2)
      } else {
        const chargeList = currencyCharges.get(c)
        if (chargeList && chargeList.length > 0) {
          for (const item of chargeList) {
            // 收付类型
            const receivable = item.receivable
            // 含税价
            const amount = item.amount
            // 汇率
            const exchangeRate = item.exchangeRate
            if (receivable) {
              revenueSum += NumberUtils.ToFixed2(amount * exchangeRate, 2)
            } else {
              costSum += NumberUtils.ToFixed2(amount * exchangeRate, 2)
            }
          }
        }
      }
    }
    revenueSum = NumberUtils.ToFixed2(revenueSum, 2)
    costSum = NumberUtils.ToFixed2(costSum, 2)
    job.estimateAccountsReceivable = revenueSum
    job.estimateAccountsPayable = costSum
    job.estimateGrossProfit = NumberUtils.ToFixed2(job.estimateAccountsReceivable - job.estimateAccountsPayable, 2)
  }
}
